import { ReactElement, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { T } from "react-translator-component";
import {
  Avatar, Badge, Box, Chip,
  IconButton, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { useAppDispatch } from "../../redux/store";
import { setErrorObj } from "../redux/errorReducer";
import apiServices from "../services/apiServices";
import AdminPageFrame from "../components/AdminPageFrame";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import LoadingModal from "../../components/LoadingModal";
import ContainerPaper from "../components/ContainerPaper";
import { colors } from "../../common/colors";
import ReadMore from "../components/ReadMore";
import AdminUserFormModal from "../components/adminUser/AdminUserFormModal";
import { convertUserImage } from "../../util/utils";
import { isSuperAdmin, isAdmin, isNavigator } from "../util/util";

export default function AdminUsers(): ReactElement {
  const dispatch = useAppDispatch();
  const { admin } = useSelector((state: any) => state.adminAuth);
  const [loader, setLoader] = useState<boolean>(false);
  const [adminUsers, setAdminUsers] = useState<any[]>([]);
  const [showConfimationModal, setShowConfimationModal] = useState<boolean>(false);
  const [editUser, setEditUser] = useState<any>();
  const [deleteAdmin, setDeleteAdmin] = useState<any>();
  const [pagination, setPagination] = useState<any>({});
  const [showFormModal, setShowFormModal] = useState<boolean>(false);

  const openAsCreate = () => {
    if (!isSuperAdmin(admin)) return

    setEditUser(undefined);
    setShowFormModal(true);
  };

  useEffect(() => {
    getAdminUsers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setShowFormModal(false);
    setEditUser(undefined);
  };

  const getAdminUsers = async (page: number) => {
    if (!page) return

    try {
      setLoader(true);
      const res = await apiServices.getAdminUsers(page);
      if (page === 1) {
        setAdminUsers(res.users);
      } else {
        setAdminUsers([...adminUsers, ...res.users]);
      }
      setPagination(res.pagination);
    } catch (error: any) {
      dispatch(setErrorObj(error))
    } finally {
      setLoader(false);
      setShowFormModal(false);
    }
  };

  const handleDelete = async (adminUser: any) => {
    if (!isSuperAdmin(admin) || !adminUser?.id) return

    setDeleteAdmin(adminUser);
    setShowConfimationModal(true);
  };

  const handleDeleteDialog = async () => {
    if (!deleteAdmin?.id) return false

    try {
      await apiServices.deleteAdminUser(deleteAdmin.id);
      getAdminUsers(1);
      return true
    } catch (error: any) {
      dispatch(setErrorObj(error))
      return false
    } finally {
      setShowConfimationModal(false);
    }
  };

  const handleEditOpen = async (user: any) => {
    if (user.id !== admin.id && !isSuperAdmin(admin)) return
    setEditUser(user);
    setShowFormModal(true);
  };

  return (
    <AdminPageFrame>
      <LoadingModal visible={loader} />

      <ContainerPaper>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="caption table">
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: "700", fontSize: 20 }} colSpan={6}>
                  {T("mng_management_users_users")}
                </TableCell>
                <TableCell align="right">
                  {isSuperAdmin(admin) && (
                    <IconButton color="inherit">
                      <Badge color="secondary">
                        <GroupAddIcon onClick={openAsCreate} />
                      </Badge>
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {adminUsers.map((adminUser: any) => (
                <TableRow key={adminUser.id}>
                  <TableCell component="th" scope="row" style={{padding: "0 0 0 16px", width: "155px"}}>
                    <Typography
                      variant="subtitle2"
                      style={{ fontWeight: "700" }}
                    >
                      {isSuperAdmin(adminUser) ? (
                        <>{T("mng_admin_user_super_admin")}</>
                      ) : (
                        <>{T(`mng_admin_user_${isAdmin(adminUser) ? "admin" : "navigator"}`)}</>
                      )}
                    </Typography>
                  </TableCell>

                  <TableCell component="th" scope="row">
                    <Box display="flex" alignItems="center">
                      <Avatar alt={adminUser?.name} src={convertUserImage(adminUser?.image)} />
                      <Box marginLeft={2}>
                        <Typography
                          variant="subtitle1"
                          style={styles.tableUserName}
                        >
                          {adminUser?.nickname}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell component="th" scope="row">
                    <Typography
                      variant="subtitle2"
                      style={{ fontWeight: "300", userSelect: "all" }}
                    >
                      {adminUser?.name}
                    </Typography>
                  </TableCell>

                  <TableCell component="th" scope="row">
                    <Typography
                      variant="subtitle2"
                      style={{ fontWeight: "300", userSelect: "all" }}
                    >
                      {adminUser?.email}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    {isNavigator(adminUser) ? (
                      <Chip
                        label={T(`mng_admin_user_navigator_${adminUser?.disabled_at ? 'disabled' : 'enabled'}`)[0]}
                        variant="outlined"
                      />
                    ) : (<></>)}
                  </TableCell>

                  <TableCell align="right">
                    {(adminUser?.id === admin.id || isSuperAdmin(admin)) && (
                      <IconButton onClick={() => handleEditOpen(adminUser)}>
                        <EditIcon color="primary" />
                      </IconButton>
                    )}
                  </TableCell>

                  <TableCell align="right">
                    {isSuperAdmin(admin) && (
                      <IconButton onClick={() => handleDelete(adminUser)}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <ReadMore
            open={pagination.next_page > 0}
            onClick={() => getAdminUsers(pagination?.next_page)}
          />

          <AdminUserFormModal
            show={showFormModal}
            editingUser={editUser}
            onClose={handleClose}
            onReload={() => getAdminUsers(1)}
          />

          <DeleteConfirmationDialog
            message={T('mng_confirmation_delete_admin_user')}
            open={showConfimationModal}
            validate={async () => true}
            onSubmit={() => handleDeleteDialog()}
            onClose={() => setShowConfimationModal(false)}
          />
        </TableContainer>
      </ContainerPaper>
    </AdminPageFrame>
  );
}

const styles = {
  noticeMessage: {
    fontSize: 14,
    color: colors.farm_red600,
    marginBottom: 5,
    fontWeight: "500",
  },
  tableUserName: { color: colors.farm_blue200, fontWeight: "600" },
}
